import React, { FC, useRef, useEffect, useState, useContext } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useRouter } from 'next/router';

import SeedLogo from 'components/global/Logo';
import {
  NavigationItem,
  NavigationProps,
} from 'components/home/TopNavigation/top-navigation.types';

import dynamic from 'next/dynamic';

import {
  GlassContainer,
  CartButton,
  NavButton,
  TopNav,
  SiteHeader,
  NavContainer,
  NavDropdown,
  tabVariants,
  tabVariantsMobile,
  listVariants,
  TabButtonGroup,
  TabButton,
  TabPill,
  GlassContainerMask,
  CartCount,
  Referral25Label,
} from 'components/home/TopNavigation/top-navigation.styled';
import NavSubLists from 'components/home/TopNavigation/nav-sub-lists';
import { UserContext } from 'context-providers/user-provider';
import { getAccountDropdownContent } from '../account-dropdown';
import { useIsOneTimePurchaser } from 'hooks/use-is-one-time-purchaser';
import { CartContext } from 'context-providers/cart-provider';
import { trackEvent } from 'lib/analytics';
import { getNavCtas, getNavState, NavStates } from 'lib/user/get-nav-state';
import { useFeatureFlags } from 'context-providers/feature-flag-provider';
import LoginDropdownPercentRollout from '../login-dropdown-percent-rollout';
import {
  useIsJourneyMember,
  useJourneyDestination,
} from 'context-providers/journey-provider';

const LoginDropdown = dynamic(
  () => import('components/home/TopNavigation/login-dropdown'),
);

const glassContainerVariants = {
  open: {
    display: 'block',
    x: 0,
    transition: {
      duration: 0.15,
      type: 'linear',
    },
  },
  closed: {
    x: '-100%',
    transition: {
      duration: 0.15,
      type: 'linear',
    },
    transitionEnd: {
      display: 'none',
    },
  },
};

const SmallNavigation: FC<NavigationProps> = ({
  mainNavigation,
  theme = 'white',
}) => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState<NavigationItem>();
  const [cartVisible, setCartVisible] = useState(true);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { userState } = useContext(UserContext);
  const { userCart, openCart } = useContext(CartContext);
  const { featureFlags } = useFeatureFlags();

  // Also gates the bookmark fetch, so members outside the experiment don't
  // pay for a request they'll never use.
  const showJourney = useIsJourneyMember();
  const journeyHref = useJourneyDestination(showJourney);
  const { isOneTimePurchaser } = useIsOneTimePurchaser();
  const accountDropdownContent = getAccountDropdownContent({
    showJourney,
    journeyHref,
    isOneTimePurchaser,
  });
  const [selectedTab, setSelectedTab] = useState('');

  const isRolloutLoginEnabled = featureFlags.isRolloutLoginEnabled;

  const navState = getNavState(userState, userCart);
  const { showGetStarted, showCart } = getNavCtas(navState, router.asPath);

  const resetTab = () => {
    setSelectedTab('');
    setCurrentTab(undefined);
  };

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      resetTab();
    };
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);

  useEffect(() => {
    if (!isOpen || !currentTab) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        resetTab();
      }
    };

    const handleClickOutside = (e: Event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        resetTab();
      }
    };

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, currentTab]);

  const handleNavButtonClick = () => {
    setIsOpen(!isOpen);

    if (userState?.email) {
      setSelectedTab('account');
      setCurrentTab(accountDropdownContent);
    }
    if (!userState?.email) {
      setSelectedTab('shop');
      setCurrentTab(getCurrentTab('shop'));
    }

    if (!isOpen) {
      setCartVisible(false);
    }
  };

  const getCurrentTab = (id: string) => {
    const mainNavigationId = mainNavigation.find((item) => item.id === id);
    if (mainNavigationId) {
      return mainNavigationId;
    }

    return { id };
  };

  const handleClick = (id: string) => {
    if (id === currentTab?.id) {
      setCurrentTab(undefined);
      setSelectedTab('');
      return;
    }
    setSelectedTab(id);
    const tab = getCurrentTab(id);
    setCurrentTab(tab);
    trackEvent('Nav Menu Click', { menu: tab?.label });
  };

  const longClickTimer = useRef<NodeJS.Timeout | null>(null);
  const longClickTriggered = useRef(false);
  const LONG_CLICK_DURATION = 800;
  const { setIsFeaturePanelVisible, isFeaturePanelVisible } = useFeatureFlags();

  const handleNavButtonTouchStart = () => {
    longClickTriggered.current = false;
    longClickTimer.current = setTimeout(() => {
      longClickTriggered.current = true;
      setIsFeaturePanelVisible &&
        setIsFeaturePanelVisible(!isFeaturePanelVisible);
    }, LONG_CLICK_DURATION);
  };

  const handleNavButtonTouchEnd = (e: React.TouchEvent) => {
    if (longClickTimer.current) {
      clearTimeout(longClickTimer.current);
      longClickTimer.current = null;
    }
    if (longClickTriggered.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <TopNav aria-label='Main'>
      <SiteHeader style={isOpen ? { flex: 1 } : {}}>
        <NavContainer className={`theme-${theme} ${isOpen ? 'is-open' : ''}`}>
          <SeedLogo
            light={isOpen || theme === 'white'}
            size='natural'
            expanded={!isOpen}
            fontSize='1.25rem'
          />
          {router.pathname !== '/checkout' && (
            <>
              <AnimatePresence key='items' mode='wait'>
                {isOpen && (
                  <div style={{ position: 'relative', width: '100%' }}>
                    <TabButtonGroup
                      variants={tabVariantsMobile}
                      initial='hidden'
                      animate={isOpen ? 'visible' : 'hidden'}
                      exit='hidden'
                    >
                      {mainNavigation.map((item) => (
                        <TabButton
                          id={`tab-${item.id}`}
                          aria-controls={`panel-${item.id}`}
                          aria-expanded={selectedTab === item.id}
                          className={selectedTab === item.id ? 'is-active' : ''}
                          key={item.id}
                          type='button'
                          onClick={() => handleClick(item.id)}
                        >
                          {item.label}
                          {selectedTab === item.id ? (
                            <TabPill
                              layoutId='tabPill'
                              id='tabPill'
                              transition={{
                                type: 'spring',
                                visualDuration: 0.2,
                                bounce: 0.2,
                              }}
                            />
                          ) : null}
                        </TabButton>
                      ))}
                      {userState?.email ? (
                        <TabButton
                          className={
                            selectedTab === 'account' ? 'is-active' : ''
                          }
                          key='account'
                          type='button'
                          onClick={() => handleClick('account')}
                        >
                          Account
                          {selectedTab === 'account' ? (
                            <TabPill
                              layoutId='tabPill'
                              id='tabPill'
                              transition={{
                                type: 'spring',
                                visualDuration: 0.2,
                                bounce: 0.2,
                              }}
                            />
                          ) : null}
                        </TabButton>
                      ) : (
                        <TabButton
                          className={selectedTab === 'login' ? 'is-active' : ''}
                          key='login'
                          type='button'
                          onClick={() => handleClick('login')}
                        >
                          Sign in
                          {selectedTab === 'login' ? (
                            <TabPill
                              layoutId='tabPill'
                              id='tabPill'
                              transition={{
                                type: 'spring',
                                visualDuration: 0.2,
                                bounce: 0.2,
                              }}
                            />
                          ) : null}
                        </TabButton>
                      )}
                    </TabButtonGroup>
                  </div>
                )}
              </AnimatePresence>
              {!isOpen && <GlassContainer />}
            </>
          )}
        </NavContainer>
      </SiteHeader>
      {router.pathname !== '/checkout' && (
        <SiteHeader>
          {cartVisible && (
            <motion.div
              variants={tabVariants}
              initial='hidden'
              animate='visible'
              exit='hidden'
            >
              {showGetStarted ? (
                <CartButton
                  url='/products'
                  className={`theme-${theme}`}
                  color={theme === 'white' ? 'snowwhite' : 'seedgreen'}
                  size='medium'
                  onClick={() => {
                    trackEvent('Button Clicked', {
                      buttonName: 'Get Started',
                      location: 'Right Nav',
                    });
                  }}
                >
                  Get Started
                </CartButton>
              ) : null}
              {showCart ? (
                <CartButton
                  className={`theme-${theme}`}
                  color={theme === 'white' ? 'snowwhite' : 'seedgreen'}
                  size='medium'
                  onClick={() => {
                    openCart();
                    trackEvent('Button Clicked', {
                      buttonName: 'Cart',
                      location: 'Right Nav',
                    });
                  }}
                >
                  Cart
                  {!userCart.isEmpty() ? (
                    <CartCount>&nbsp;{userCart.count()}</CartCount>
                  ) : null}
                </CartButton>
              ) : null}
              {navState === NavStates.LoggedInEmptyCart ? (
                <CartButton
                  url='/account/refer?traffic_source=nav_earn'
                  color={theme === 'white' ? 'snowwhite' : 'seedgreen'}
                  size='medium'
                  className={`theme-${theme} ${
                    userState?.experiments?.referral_nav ? 'with-label' : ''
                  }`}
                >
                  Refer{' '}
                  {userState?.experiments?.referral_nav ? (
                    <Referral25Label size='small'>$25</Referral25Label>
                  ) : null}
                </CartButton>
              ) : null}
            </motion.div>
          )}

          <NavButton
            className={`theme-${theme} ${isOpen ? 'is-open' : ''}`}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
            animate={isOpen ? 'open' : 'closed'}
            onClick={() => handleNavButtonClick()}
            {...(process.env.NEXT_PUBLIC_STAGING === 'true'
              ? {
                  onTouchStart: handleNavButtonTouchStart,
                  onTouchEnd: handleNavButtonTouchEnd,
                  onTouchCancel: handleNavButtonTouchEnd,
                }
              : {})}
          >
            <svg
              className='svg'
              viewBox='0 0 24 24'
              width='24'
              height='24'
              aria-hidden='true'
            >
              <polyline
                className='bar bar1'
                stroke='currentColor'
                strokeWidth='2'
                points='3.5,9 20.5,9'
              />
              <polyline
                className='bar bar2'
                stroke='currentColor'
                strokeWidth='2'
                points='3.5,15 20.5,15'
              />
            </svg>
          </NavButton>
        </SiteHeader>
      )}
      {router.pathname !== '/checkout' && (
        <GlassContainerMask>
          <GlassContainer
            initial='closed'
            className={isOpen ? 'is-open' : ''}
            animate={isOpen ? 'open' : 'closed'}
            variants={glassContainerVariants}
            key='small-navigation'
            onAnimationComplete={() => {
              if (!isOpen) {
                setTimeout(() => {
                  setCartVisible(true);
                }, 200);
              }
            }}
          />
        </GlassContainerMask>
      )}
      {router.pathname !== '/checkout' && (
        <AnimatePresence key='tablist' mode='wait'>
          {isOpen && currentTab && (
            <NavDropdown
              ref={dropdownRef}
              id={`panel-${currentTab.id}`}
              aria-labelledby={`tab-${currentTab.id}`}
              variants={tabVariants}
              initial='hidden'
              animate='visible'
              exit='hidden'
            >
              <AnimatePresence key={currentTab.id} mode='wait'>
                {currentTab.id === 'login' ? (
                  isRolloutLoginEnabled ? (
                    <LoginDropdownPercentRollout
                      id='login'
                      variants={listVariants}
                    />
                  ) : (
                    <LoginDropdown id='login' variants={listVariants} />
                  )
                ) : currentTab.id === 'account' ? (
                  <NavSubLists currentTab={accountDropdownContent} />
                ) : (
                  <NavSubLists currentTab={currentTab} />
                )}
              </AnimatePresence>
            </NavDropdown>
          )}
        </AnimatePresence>
      )}
    </TopNav>
  );
};

export default SmallNavigation;
